import { z } from 'zod';
import {
  CycleTimeResultSchema,
  FrictionMapResultSchema,
  WorkPathResultSchema,
} from '../../libs/analysis/src/contracts/workflow';
import { RepoScanReviewHealthSchema } from '../impact/repo-scan';
import {
  ReviewQueueResultSchema,
  ReviewQueueWhatIfResultSchema,
} from '../review-queue';
import { GitHubCoverageSchema } from './github-coverage';

export const ReviewInsightCoverageSchema = z.object({
  mirror: z.object({
    source: z.enum(['app-mirror', 'pat-mirror']),
    status: z.enum(['loaded', 'unavailable']),
    freshAt: z.string().nullable(),
    pullRequests: z.number().int().nonnegative(),
  }),
  provider: z.object({
    source: z.enum(['github-app', 'pat']).nullable(),
    attempted: z.boolean(),
    requestCount: z.number().int().nonnegative(),
  }),
  scans: z.object({
    requested: z.number().int().nonnegative(),
    measured: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
  }),
});

export const ReviewHealthByRepoSchema = z.object({
  repo: z.string().min(1),
  scannedAt: z.string(),
  reviewHealth: RepoScanReviewHealthSchema.nullable(),
});

export const ReviewWorkflowInsightSchema = z.object({
  cycleTime: CycleTimeResultSchema,
  workPath: WorkPathResultSchema,
  friction: FrictionMapResultSchema,
  reviewHealth: z.array(ReviewHealthByRepoSchema),
  coverage: ReviewInsightCoverageSchema,
  partialReasons: z.array(z.string()),
  generatedAt: z.string(),
});

export const ReviewQueueReviewerSchema = z.object({
  login: z.string().min(1),
  personToken: z.string().min(1),
  displayName: z.string().min(1),
  confidence: z.enum(['explicit', 'email', 'name', 'single-source']),
});

/** Stated first so no consumer has to infer completeness from nested coverage. */
export const ReviewQueueCompletenessSchema = z.object({
  status: z.enum(['full', 'partial']),
  reasons: z.array(z.string()),
});

export const ReviewQueueResponseSchema = z.object({
  completeness: ReviewQueueCompletenessSchema,
  githubCoverage: GitHubCoverageSchema.default({
    source: 'app',
    completeness: 'full',
    reasons: [],
  }),
  queue: z.union([ReviewQueueResultSchema, ReviewQueueWhatIfResultSchema]),
  workflow: ReviewWorkflowInsightSchema,
  reviewers: z.array(ReviewQueueReviewerSchema),
});

export type ReviewInsightCoverage = z.infer<typeof ReviewInsightCoverageSchema>;
export type ReviewQueueCompleteness = z.infer<
  typeof ReviewQueueCompletenessSchema
>;
export type ReviewWorkflowInsight = z.infer<typeof ReviewWorkflowInsightSchema>;
export type ReviewQueueResponse = z.infer<typeof ReviewQueueResponseSchema>;
